import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Spinner, Button } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import VersionFooter from '../components/VersionFooter';
import { getSmartRedirectPath } from '../shared/accountCache';

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError(t('auth.verifyEmail.invalidLink'));
      setLoading(false);
      return;
    }

    verifyEmailToken();
  }, [token, t]);

  const verifyEmailToken = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiCall(
        `/api/set?action=${ApiActions.VERIFY_EMAIL}`,
        { token },
        'POST'
      );

      if (response.success) {
        setSuccess(true);
        
        // If user is logged in, update their verification status
        // Backend already validated that the token belongs to the logged-in user
        const cachedUser = localStorage.getItem('user');
        const userIsLoggedIn = cachedUser && localStorage.getItem('token');
        setIsLoggedIn(!!userIsLoggedIn);
        
        if (userIsLoggedIn) {
          try {
            const userData = JSON.parse(cachedUser);
            // Backend validated token ownership, so we can safely update verification status
            userData.emailVerified = true;
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Navigate using smart redirect logic after 0.5 seconds
            setTimeout(async () => {
              const redirectPath = await getSmartRedirectPath(userData);
              console.error('[VerifyEmail] Redirecting to:', redirectPath);
              window.location.href = redirectPath;
            }, 500);
          } catch (e) {
            console.error('Error updating cached user data:', e);
          }
        }
      } else {
        // Use error code from backend to get translated message
        const errorKey = response.errorCode === 'INVALID_OR_EXPIRED_TOKEN'
          ? 'auth.verifyEmail.invalidOrExpiredToken'
          : response.errorCode === 'TOKEN_REQUIRED'
          ? 'auth.verifyEmail.invalidLink'
          : response.errorCode === 'VERIFICATION_ERROR'
          ? 'auth.verifyEmail.errorOccurred'
          : 'auth.verifyEmail.verificationFailed';
        setError(t(errorKey, { defaultValue: response.message || t('auth.verifyEmail.verificationFailed') }));
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(t('auth.verifyEmail.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container className="py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Card className="shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
            <Card.Body className="p-5 text-center">
              {loading ? (
                <>
                  <Spinner animation="border" role="status" className="mb-3">
                    <span className="visually-hidden">{t('auth.verifyEmail.verifying')}</span>
                  </Spinner>
                  <h3>{t('auth.verifyEmail.verifyingTitle')}</h3>
                  <p className="text-muted">{t('auth.verifyEmail.verifyingMessage')}</p>
                </>
              ) : success ? (
                <>
                  <div className="mb-4">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                  </div>
                  <h3 className="mb-3">{t('auth.verifyEmail.successTitle')}</h3>
                  <p className="text-muted mb-4">
                    {isLoggedIn ? (
                      <>
                        {t('auth.verifyEmail.successMessageLoggedIn')}
                        <br />
                        <Spinner animation="border" size="sm" className="mt-2 me-2" />
                        {t('auth.verifyEmail.redirecting')}
                      </>
                    ) : (
                      <>{t('auth.verifyEmail.successMessageNotLoggedIn')}</>
                    )}
                  </p>
                  {!isLoggedIn && (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => navigate('/')}
                    >
                      {t('auth.verifyEmail.goToLogin')}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '4rem' }}></i>
                  </div>
                  <h3 className="mb-3">{t('auth.verifyEmail.failedTitle')}</h3>
                  <Alert variant="danger">
                    {error}
                  </Alert>
                  <p className="text-muted mb-4">
                    {t('auth.verifyEmail.failedMessage')}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/')}
                  >
                    {t('auth.verifyEmail.backToHome')}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </Container>
      <VersionFooter />
    </>
  );
}

